import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Minus, Plus, Heart, Share2, Truck, RotateCcw, Shield, Gift, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { setProductSEO, setNotFoundSEO } from '@/utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [artisan, setArtisan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [giftWrap, setGiftWrap] = useState(false);
  const [giftMessage, setGiftMessage] = useState('');
  const [pointsToEarn, setPointsToEarn] = useState(0);
  const { addToCart, loading: cartLoading } = useCart();
  const { formatPrice, currency } = useCurrency();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    fetchProduct();
  }, [slug, currency]);

  // Set SEO when product loads (or not-found SEO when missing)
  useEffect(() => {
    if (product) {
      setProductSEO(product);
    } else if (!loading) {
      setNotFoundSEO('Product Not Found');
    }
  }, [product, loading]);

  // Calculate loyalty points - simply 1% of displayed price
  useEffect(() => {
    if (selectedVariant && selectedVariant.price > 0) {
      const totalAmount = selectedVariant.price * quantity;
      const points = Math.floor(totalAmount * 0.01);
      setPointsToEarn(points);
    }
  }, [selectedVariant, quantity]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${slug}`, { params: { currency } });
      setProduct(response.data.product);
      setRelatedProducts(response.data.related_products || []);
      setReviews(response.data.reviews || []);
      setArtisan(response.data.artisan);
      
      if (response.data.product?.variants?.length) {
        setSelectedVariant(response.data.product.variants[0]);
      } else if (response.data.product) {
        // Product has no variants - create a fallback so add-to-cart works
        setSelectedVariant({
          id: 'default',
          name: 'Default',
          price: response.data.product.price || 0,
          inventory_quantity: 100
        });
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      // No toast — the not-found UI (with data-testid=product-not-found) already
      // communicates the state and doesn't need to overlap the header nav.
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) {
      toast.error('Please select a variant');
      return;
    }
    
    try {
      await addToCart(product.id, selectedVariant.id, quantity, giftWrap, giftMessage || null);
      toast.success('Added to cart', {
        description: `${product.name} has been added to your cart.`
      });
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to add to cart';
      toast.error(detail);
    }
  };

  if (loading) {
    return (
      <div className="pt-32 pb-24 chaleur-container">
        <div className="grid md:grid-cols-2 gap-12">
          <div className="aspect-[3/4] bg-secondary animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-secondary rounded w-3/4" />
            <div className="h-6 bg-secondary rounded w-1/4" />
            <div className="h-32 bg-secondary rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-32 pb-24 chaleur-container text-center" data-testid="product-not-found">
        <h1 className="text-2xl mb-4">Product not found</h1>
        <p className="text-muted-foreground mb-6">The item you&apos;re looking for is no longer available.</p>
        <Button asChild>
          <Link to="/shop">Back to Shop</Link>
        </Button>
      </div>
    );
  }

  const images = product.images?.length ? product.images.map(img => 
    img.startsWith('/') ? img : img
  ) : ['/images/products/IMG_6195.jpg'];

  return (
    <div className="pt-32 pb-24" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="chaleur-container mb-8">
        <nav className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/shop" className="hover:text-foreground">Shop</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-foreground">{product.name}</span>
        </nav>
      </div>

      {/* Product Details */}
      <div className="chaleur-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Gallery */}
          <div className="space-y-4">
            <div className="aspect-[3/4] overflow-hidden bg-secondary">
              <img 
                src={images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`aspect-square overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:sticky lg:top-32 lg:self-start space-y-8">
            {/* Header */}
            <div>
              <p className="caption text-primary mb-2">{product.craft_technique || 'Handcrafted'}</p>
              <h1 className="text-3xl md:text-4xl font-light mb-4">{product.name}</h1>
              <div className="flex items-center gap-4">
                <span className="text-2xl">{formatPrice(selectedVariant?.price)}</span>
                {selectedVariant?.compare_at_price &&
                  selectedVariant.compare_at_price > selectedVariant.price &&
                  ((selectedVariant.compare_at_price - selectedVariant.price) / selectedVariant.compare_at_price) < 0.5 && (
                  <span className="text-lg text-muted-foreground line-through">
                    {formatPrice(selectedVariant.compare_at_price)}
                  </span>
                )}
              </div>
              {product.average_rating > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={i < Math.round(product.average_rating) ? 'text-primary' : 'text-muted-foreground/30'}>★</span>
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">({product.review_count} reviews)</span>
                </div>
              )}
              
              {/* Loyalty Points Earned */}
              {pointsToEarn > 0 && (
                <div className="flex items-center gap-2 mt-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                  <Award className="w-5 h-5 text-amber-600" />
                  <span className="text-sm">
                    <span className="font-semibold text-amber-700">Earn {pointsToEarn} Chaleur Points</span>
                    <span className="text-amber-600 ml-1">(worth ₹{pointsToEarn})</span>
                  </span>
                  <Link to="/chaleur-club" className="text-xs text-amber-600 hover:text-amber-700 ml-auto underline">
                    Learn more
                  </Link>
                </div>
              )}
            </div>

            {/* Short Description */}
            <p className="text-muted-foreground leading-relaxed">
              {product.short_description || product.description?.slice(0, 200)}
            </p>

            {/* Variants */}
            {product.variants?.length > 1 && (
              <div>
                <label className="text-sm font-medium mb-3 block">Select Variant</label>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map(variant => (
                    <button
                      key={variant.id}
                      onClick={() => setSelectedVariant(variant)}
                      className={`px-4 py-2 border text-sm transition-colors ${
                        selectedVariant?.id === variant.id 
                          ? 'border-primary bg-primary text-primary-foreground' 
                          : 'border-border hover:border-primary'
                      }`}
                    >
                      {variant.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <label className="text-sm font-medium mb-3 block">Quantity</label>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-border">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-secondary transition-colors"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center hover:bg-secondary transition-colors"
                    aria-label="Increase quantity"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {selectedVariant?.inventory_quantity === 0 && (
                  <span className="text-sm text-muted-foreground">Currently unavailable</span>
                )}
              </div>
            </div>

            {/* Gift Options */}
            <div className="p-4 bg-accent/20 border border-accent/30">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox 
                  checked={giftWrap} 
                  onCheckedChange={setGiftWrap}
                  className="rounded-none"
                />
                <div className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-primary" />
                  <span className="text-sm">Add luxury gift wrapping (+{formatPrice(199)})</span>
                </div>
              </label>
              {giftWrap && (
                <div className="mt-4">
                  <Input
                    placeholder="Add a personal message (optional)"
                    value={giftMessage}
                    onChange={(e) => setGiftMessage(e.target.value)}
                    className="input-chaleur"
                  />
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <Button 
                className="btn-chaleur-primary flex-1" 
                onClick={handleAddToCart}
                disabled={cartLoading || !selectedVariant}
                data-testid="add-to-cart-btn"
              >
                {cartLoading ? 'Adding...' : 'Add to Cart'}
              </Button>
              <button className="w-12 h-12 border border-border flex items-center justify-center hover:border-primary transition-colors" aria-label="Add to wishlist">
                <Heart className="w-5 h-5" />
              </button>
              <button className="w-12 h-12 border border-border flex items-center justify-center hover:border-primary transition-colors" aria-label="Share">
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
              <div className="text-center">
                <Truck className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Free Shipping<br/>Above ₹5,000</p>
              </div>
              <div className="text-center">
                <RotateCcw className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Easy Returns<br/>Within 15 Days</p>
              </div>
              <div className="text-center">
                <Shield className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Authentic<br/>Craftsmanship</p>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="mt-24">
          <Tabs defaultValue="story" className="w-full">
            <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0">
              <TabsTrigger value="story" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-4">
                The Story
              </TabsTrigger>
              <TabsTrigger value="details" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-4">
                Materials & Care
              </TabsTrigger>
              <TabsTrigger value="impact" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-4">
                Impact
              </TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-4">
                Reviews ({product.review_count || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="story" className="pt-8">
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-light mb-4">The Story Behind</h3>
                  <div className="prose text-muted-foreground">
                    {product.story_behind || product.description || 'Every Chaleur piece carries a story of tradition, skill, and purpose.'}
                  </div>
                  {artisan && (
                    <Link to={`/artisans/${artisan.slug}`} className="inline-flex items-center gap-2 mt-6 text-primary hover:text-primary/80">
                      <span>Meet the artisan: {artisan.name}</span>
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
                {artisan && (
                  <div className="aspect-square overflow-hidden">
                    <img src={artisan.image} alt={artisan.name} className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="details" className="pt-8">
              <div className="max-w-2xl">
                <div className="prose text-muted-foreground">
                  {product.materials_care || (
                    <>
                      <h4>Materials</h4>
                      <ul>
                        <li>100% Pure {selectedVariant?.material || 'Silk'}</li>
                        <li>Handwoven by master artisans</li>
                        <li>Natural, eco-friendly dyes</li>
                      </ul>
                      <h4>Care Instructions</h4>
                      <ul>
                        <li>Dry clean recommended</li>
                        <li>Store in a cool, dry place</li>
                        <li>Avoid direct sunlight for extended periods</li>
                        <li>Iron on low heat if needed</li>
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="impact" className="pt-8">
              <div className="max-w-2xl">
                <h3 className="text-2xl font-light mb-4">Your Purchase Helps</h3>
                <p className="text-muted-foreground mb-6">
                  {product.impact_statement || 'Every purchase supports artisan communities, providing sustainable livelihoods and preserving traditional crafts for future generations.'}
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-4 bg-secondary/50">
                    <div className="text-2xl font-light text-primary mb-1">2</div>
                    <p className="text-sm text-muted-foreground">Artisan families supported</p>
                  </div>
                  <div className="p-4 bg-secondary/50">
                    <div className="text-2xl font-light text-primary mb-1">15hrs</div>
                    <p className="text-sm text-muted-foreground">Of skilled craftsmanship</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="reviews" className="pt-8">
              {reviews.length > 0 ? (
                <div className="space-y-6">
                  {reviews.map(review => (
                    <div key={review.id} className="border-b border-border pb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className={i < review.rating ? 'text-primary' : 'text-muted-foreground/30'}>★</span>
                          ))}
                        </div>
                        {review.is_verified_purchase && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5">Verified Purchase</span>
                        )}
                      </div>
                      <h4 className="font-medium">{review.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{review.content}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        By {review.customer_name} on {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No reviews yet. Be the first to review this product.</p>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-24">
            <h2 className="text-2xl font-light mb-8">You May Also Like</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((p, idx) => (
                <Link 
                  key={p.id} 
                  to={`/product/${p.slug}`}
                  className="product-card"
                >
                  <div className="product-card-image">
                    <img 
                      src={p.images?.[0] || 'https://images.unsplash.com/photo-1585007326391-d4bfd01fb320?w=400&q=80'}
                      alt={p.name}
                    />
                  </div>
                  <div className="mt-4 space-y-1">
                    <h3 className="text-sm font-medium line-clamp-1">{p.name}</h3>
                    <span className="text-sm">{formatPrice(p.variants?.[0]?.price)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPage;
